/**
 * `div.bars` → `bar-chart` (process/08-COMPONENT-CATALOGUE.md). A stacked vertical bar per day,
 * segments succeeded/partial/failed — the shape `Prototype_v2.html`'s own `#chart-bars` draws for
 * `v-dashboard`'s "Backup jobs history" panel. Values are read straight off `BackupSummary.daily`
 * (rule 1, process/09-SCREEN-SPECS/v-dashboard.md) — nothing here is derived or totalled.
 */
export interface BarChartDay {
  readonly date: string
  readonly succeeded: number
  readonly partial: number
  readonly failed: number
}

export function BarChart({ days }: { readonly days: readonly BarChartDay[] }) {
  const max = Math.max(1, ...days.map((d) => d.succeeded + d.partial + d.failed))
  const gridlines = [max, Math.round((max * 2) / 3), Math.round(max / 3), 0]

  return (
    <div data-cmp="bar-chart">
      <div className="flex gap-4 text-small text-tx2 mb-3">
        <span className="flex items-center gap-2">
          <i className="inline-block w-3 h-3 rounded-pill bg-ok" />
          Success
        </span>
        <span className="flex items-center gap-2">
          <i className="inline-block w-3 h-3 rounded-pill bg-warn" />
          Partial
        </span>
        <span className="flex items-center gap-2">
          <i className="inline-block w-3 h-3 rounded-pill bg-fail" />
          Failed
        </span>
      </div>
      <div className="relative flex items-end gap-2 h-[180px] border-l border-b border-bd pl-8 pb-1">
        <div className="absolute left-0 top-0 bottom-1 flex flex-col justify-between text-micro text-tx2 w-6 text-right pr-1">
          {gridlines.map((g, i) => (
            <span key={i} data-cmp="bar-gridline">
              {g}
            </span>
          ))}
        </div>
        {days.map((d) => {
          const total = d.succeeded + d.partial + d.failed
          const scale = total === 0 ? 0 : 100 / max
          return (
            <div key={d.date} className="flex-1 flex flex-col justify-end h-full" title={`${d.date}: ${d.succeeded} succeeded, ${d.partial} partial, ${d.failed} failed`}>
              {d.failed > 0 && <div className="w-full bg-fail" style={{ height: `${d.failed * scale}%` }} />}
              {d.partial > 0 && <div className="w-full bg-warn" style={{ height: `${d.partial * scale}%` }} />}
              {d.succeeded > 0 && <div className="w-full bg-ok" style={{ height: `${d.succeeded * scale}%` }} />}
            </div>
          )
        })}
      </div>
      <div className="flex gap-2 pl-8 mt-1">
        {days.map((d) => (
          <span key={d.date} className="flex-1 text-center text-micro text-tx2">
            {d.date.slice(5)}
          </span>
        ))}
      </div>
      <div className="text-center text-micro text-tx2 font-semibold mt-1">Date</div>
    </div>
  )
}
